// Supabase-backed storage for Masjid Baitul Gufron admin settings
// Replaces localStorage with persistent real-time database

import { createClient } from '@/lib/supabase/client';
import type { IqomahSettings, RunningTextItem, PhotoItem, ThemeId, AdminSettings } from './storage';

export type { IqomahSettings, RunningTextItem, PhotoItem, ThemeId, AdminSettings };

// ─── Hadist / Kata-kata Mutiara ──────────────────────────────────────────────

export interface HadistItem {
  id: string;
  text: string;
  source: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface AppSettings {
  provinsi: string;
  kabkota: string;
  bgOpacity: number; // 0-100
  iqomahBlinkDuration: number; // seconds, default 10
  prayerModeDuration: number;  // minutes, default 10
  hadistFontSize: number; // px, default 48
  mutiaraFontSize: number; // px, default 48
}

// ─── Iqomah Settings ────────────────────────────────────────────────────────

export async function getIqomahSettings(): Promise<IqomahSettings> {
  const supabase = createClient();
  if (!supabase) return { subuh: 10, dzuhur: 15, ashar: 10, maghrib: 8, isya: 10 };
  const { data, error } = await supabase
    .from('masjid_iqomah_settings')
    .select('*')
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    return { subuh: 10, dzuhur: 15, ashar: 10, maghrib: 8, isya: 10 };
  }
  return {
    subuh: data.subuh,
    dzuhur: data.dzuhur,
    ashar: data.ashar,
    maghrib: data.maghrib,
    isya: data.isya,
  };
}

export async function saveIqomahSettings(settings: IqomahSettings): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  const { data: existing } = await supabase
    .from('masjid_iqomah_settings')
    .select('id')
    .limit(1)
    .maybeSingle();

  if (existing?.id) {
    await supabase
      .from('masjid_iqomah_settings')
      .update({ ...settings, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
  } else {
    await supabase.from('masjid_iqomah_settings').insert({ ...settings });
  }
}

// ─── App Settings (Location + Opacity) ──────────────────────────────────────

export async function getAppSettings(): Promise<AppSettings> {
  const supabase = createClient();
  if (!supabase) return { provinsi: 'Jawa Timur', kabkota: 'Kota Malang', bgOpacity: 35, iqomahBlinkDuration: 10, prayerModeDuration: 10, hadistFontSize: 48, mutiaraFontSize: 48 };
  const { data, error } = await supabase
    .from('masjid_app_settings')
    .select('*')
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    return { provinsi: 'Jawa Timur', kabkota: 'Kota Malang', bgOpacity: 35, iqomahBlinkDuration: 10, prayerModeDuration: 10, hadistFontSize: 48, mutiaraFontSize: 48 };
  }
  return {
    provinsi: data.provinsi,
    kabkota: data.kabkota,
    bgOpacity: data.bg_opacity,
    iqomahBlinkDuration: data.iqomah_blink_duration ?? 10,
    prayerModeDuration: data.prayer_mode_duration ?? 10,
    hadistFontSize: data.hadist_font_size ?? 48,
    mutiaraFontSize: data.mutiara_font_size ?? 48,
  };
}

export async function saveAppSettings(settings: AppSettings): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  const { data: existing } = await supabase
    .from('masjid_app_settings')
    .select('id')
    .limit(1)
    .maybeSingle();

  const dbData = {
    provinsi: settings.provinsi,
    kabkota: settings.kabkota,
    bg_opacity: settings.bgOpacity,
    iqomah_blink_duration: settings.iqomahBlinkDuration ?? 10,
    prayer_mode_duration: settings.prayerModeDuration ?? 10,
    hadist_font_size: settings.hadistFontSize ?? 48,
    mutiara_font_size: settings.mutiaraFontSize ?? 48,
    updated_at: new Date().toISOString(),
  };

  if (existing?.id) {
    await supabase.from('masjid_app_settings').update(dbData).eq('id', existing.id);
  } else {
    await supabase.from('masjid_app_settings').insert(dbData);
  }
}

// ─── Hadist ──────────────────────────────────────────────────────────────────

export async function getHadistList(): Promise<HadistItem[]> {
  const supabase = createClient();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('masjid_hadist')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error || !data) return [];
  return data.map((row) => ({
    id: row.id,
    text: row.text,
    source: row.source || '',
    active: row.active,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
  }));
}

export async function addHadist(item: Omit<HadistItem, 'id' | 'sortOrder' | 'createdAt'>): Promise<HadistItem | null> {
  const supabase = createClient();
  if (!supabase) return null;
  const { data: existing } = await supabase
    .from('masjid_hadist')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();

  const nextOrder = (existing?.sort_order ?? 0) + 1;

  const { data, error } = await supabase
    .from('masjid_hadist')
    .insert({
      text: item.text,
      source: item.source,
      active: item.active,
      sort_order: nextOrder,
    })
    .select()
    .single();

  if (error || !data) return null;
  return {
    id: data.id,
    text: data.text,
    source: data.source || '',
    active: data.active,
    sortOrder: data.sort_order,
    createdAt: data.created_at,
  };
}

export async function updateHadist(id: string, updates: Partial<Pick<HadistItem, 'text' | 'source' | 'active'>>): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  const dbUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.text !== undefined) dbUpdates.text = updates.text;
  if (updates.source !== undefined) dbUpdates.source = updates.source;
  if (updates.active !== undefined) dbUpdates.active = updates.active;
  await supabase.from('masjid_hadist').update(dbUpdates).eq('id', id);
}

export async function deleteHadist(id: string): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  await supabase.from('masjid_hadist').delete().eq('id', id);
}

// ─── Running Texts ───────────────────────────────────────────────────────────

export async function getRunningTexts(): Promise<RunningTextItem[]> {
  const supabase = createClient();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('masjid_running_texts')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error || !data) return [];
  return data.map((row) => ({
    id: row.id,
    text: row.text,
    active: row.active,
    createdAt: row.created_at,
  }));
}

export async function addRunningText(item: Omit<RunningTextItem, 'id'>): Promise<RunningTextItem | null> {
  const supabase = createClient();
  if (!supabase) return null;
  const { data: existing } = await supabase
    .from('masjid_running_texts')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();

  const nextOrder = (existing?.sort_order ?? 0) + 1;

  const { data, error } = await supabase
    .from('masjid_running_texts')
    .insert({
      text: item.text,
      active: item.active,
      sort_order: nextOrder,
      created_at: item.createdAt,
    })
    .select()
    .single();

  if (error || !data) return null;
  return { id: data.id, text: data.text, active: data.active, createdAt: data.created_at };
}

export async function updateRunningText(id: string, updates: Partial<RunningTextItem>): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  const dbUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.text !== undefined) dbUpdates.text = updates.text;
  if (updates.active !== undefined) dbUpdates.active = updates.active;

  await supabase.from('masjid_running_texts').update(dbUpdates).eq('id', id);
}

export async function deleteRunningText(id: string): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  await supabase.from('masjid_running_texts').delete().eq('id', id);
}

// ─── Photos ──────────────────────────────────────────────────────────────────

export async function getPhotos(): Promise<PhotoItem[]> {
  const supabase = createClient();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('masjid_photos')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error || !data) return [];
  return data.map((row) => ({
    id: row.id,
    url: row.url,
    name: row.name,
    uploadedAt: row.uploaded_at,
  }));
}

export async function addPhoto(photo: Omit<PhotoItem, 'id'>, storagePath?: string): Promise<PhotoItem | null> {
  const supabase = createClient();
  if (!supabase) return null;
  const { data: existing } = await supabase
    .from('masjid_photos')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();

  const nextOrder = (existing?.sort_order ?? 0) + 1;

  const { data, error } = await supabase
    .from('masjid_photos')
    .insert({
      name: photo.name,
      url: photo.url,
      storage_path: storagePath || null,
      sort_order: nextOrder,
      uploaded_at: photo.uploadedAt,
    })
    .select()
    .single();

  if (error || !data) return null;
  return { id: data.id, url: data.url, name: data.name, uploadedAt: data.uploaded_at };
}

export async function deletePhoto(id: string): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  const { data } = await supabase
    .from('masjid_photos')
    .select('storage_path')
    .eq('id', id)
    .maybeSingle();

  if (data?.storage_path) {
    await supabase.storage.from('masjid-photos').remove([data.storage_path]);
  }

  await supabase.from('masjid_photos').delete().eq('id', id);
}

export async function uploadPhotoFile(file: File): Promise<{ url: string; path: string } | null> {
  const supabase = createClient();
  if (!supabase) return null;
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `masjid/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage
    .from('masjid-photos')
    .upload(path, file, { cacheControl: '3600', upsert: false });

  if (error) return null;

  const { data: { publicUrl } } = supabase.storage.from('masjid-photos').getPublicUrl(path);
  return { url: publicUrl, path };
}

// ─── Display Informasi ───────────────────────────────────────────────────────

export interface DisplayInformasiItem {
  id: string;
  judul: string;
  imageUrl: string;
  storagePath?: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
}

export async function getDisplayInformasiList(): Promise<DisplayInformasiItem[]> {
  const supabase = createClient();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('masjid_display_informasi')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error || !data) return [];
  return data.map((row) => ({
    id: row.id,
    judul: row.judul,
    imageUrl: row.image_url,
    storagePath: row.storage_path || undefined,
    active: row.active,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
  }));
}

export async function addDisplayInformasi(
  item: Omit<DisplayInformasiItem, 'id' | 'sortOrder' | 'createdAt'>
): Promise<DisplayInformasiItem | null> {
  const supabase = createClient();
  if (!supabase) return null;
  const { data: existing } = await supabase
    .from('masjid_display_informasi')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();

  const nextOrder = (existing?.sort_order ?? 0) + 1;

  const { data, error } = await supabase
    .from('masjid_display_informasi')
    .insert({
      judul: item.judul,
      image_url: item.imageUrl,
      storage_path: item.storagePath || null,
      active: item.active,
      sort_order: nextOrder,
    })
    .select()
    .single();

  if (error || !data) return null;
  return {
    id: data.id,
    judul: data.judul,
    imageUrl: data.image_url,
    storagePath: data.storage_path || undefined,
    active: data.active,
    sortOrder: data.sort_order,
    createdAt: data.created_at,
  };
}

export async function updateDisplayInformasi(
  id: string,
  updates: Partial<Pick<DisplayInformasiItem, 'judul' | 'active'>>
): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  const dbUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.judul !== undefined) dbUpdates.judul = updates.judul;
  if (updates.active !== undefined) dbUpdates.active = updates.active;
  await supabase.from('masjid_display_informasi').update(dbUpdates).eq('id', id);
}

export async function deleteDisplayInformasi(id: string): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  const { data } = await supabase
    .from('masjid_display_informasi')
    .select('storage_path')
    .eq('id', id)
    .maybeSingle();

  if (data?.storage_path) {
    await supabase.storage.from('masjid-display-informasi').remove([data.storage_path]);
  }

  await supabase.from('masjid_display_informasi').delete().eq('id', id);
}

export async function uploadDisplayInformasiFile(
  file: File
): Promise<{ url: string; path: string } | null> {
  const supabase = createClient();
  if (!supabase) return null;
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `informasi/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage
    .from('masjid-display-informasi')
    .upload(path, file, { cacheControl: '3600', upsert: false });

  if (error) return null;

  const {
    data: { publicUrl },
  } = supabase.storage.from('masjid-display-informasi').getPublicUrl(path);
  return { url: publicUrl, path };
}

// ─── Display Theme ───────────────────────────────────────────────────────────

export async function getSelectedTheme(): Promise<ThemeId> {
  const supabase = createClient();
  if (!supabase) return 'dark-green';
  const { data, error } = await supabase
    .from('masjid_display_theme')
    .select('selected_theme')
    .limit(1)
    .maybeSingle();

  if (error || !data) return 'dark-green';
  return (data.selected_theme as ThemeId) || 'dark-green';
}

export async function saveSelectedTheme(themeId: ThemeId): Promise<void> {
  const supabase = createClient();
  if (!supabase) return;
  const { data: existing } = await supabase
    .from('masjid_display_theme')
    .select('id')
    .limit(1)
    .maybeSingle();

  if (existing?.id) {
    await supabase
      .from('masjid_display_theme')
      .update({ selected_theme: themeId, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
  } else {
    await supabase.from('masjid_display_theme').insert({ selected_theme: themeId });
  }
}

// ─── Full Settings (composite) ───────────────────────────────────────────────

export interface FullAdminSettings {
  iqomah: IqomahSettings;
  runningTexts: RunningTextItem[];
  photos: PhotoItem[];
  selectedTheme: ThemeId;
  hadistList: HadistItem[];
  appSettings: AppSettings;
  displayInformasiList: DisplayInformasiItem[];
}

export async function getAllSettings(): Promise<FullAdminSettings> {
  const [iqomah, runningTexts, photos, selectedTheme, hadistList, appSettings, displayInformasiList] =
    await Promise.all([
      getIqomahSettings(),
      getRunningTexts(),
      getPhotos(),
      getSelectedTheme(),
      getHadistList(),
      getAppSettings(),
      getDisplayInformasiList(),
    ]);
  return { iqomah, runningTexts, photos, selectedTheme, hadistList, appSettings, displayInformasiList };
}

// ─── Real-time subscription helper ──────────────────────────────────────────

export type SettingsChangeCallback = (settings: FullAdminSettings) => void;

export function subscribeToSettings(callback: SettingsChangeCallback): () => void {
  const supabase = createClient();

  if (!supabase) {
    return () => {};
  }

  let channel: ReturnType<typeof supabase.channel> | null = null;

  const refresh = () => {
    getAllSettings().then(callback);
  };

  channel = supabase
    .channel('masjid-settings-changes')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'masjid_iqomah_settings' }, refresh)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'masjid_running_texts' }, refresh)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'masjid_photos' }, refresh)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'masjid_display_theme' }, refresh)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'masjid_hadist' }, refresh)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'masjid_app_settings' }, refresh)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'masjid_display_informasi' }, refresh)
    .subscribe();

  return () => {
    if (channel) {
      supabase.removeChannel(channel);
    }
  };
}
